import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer, AlertCircle } from "lucide-react";

/**
 * Druckbare Team-QR-Übersicht — alle Spieler eines Teams als QR-Grid
 * auf A4-Seiten für Kabinen-Aushang / Trainingsauftakt.
 *
 * URL: /teams/:teamId/qr-print
 */
export default function TeamQRPrint() {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [t, ps] = await Promise.all([
          api.get(`/teams/${teamId}`),
          api.get(`/teams/${teamId}/players`),
        ]);
        setTeam(t.data);
        setPlayers(ps.data.players);
      } catch (e) {
        setErr(formatApiError(e));
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [teamId]);

  const testLink = (token) => `${window.location.origin}/test/${token}`;

  const pending = players.filter((p) => p.test_status !== "completed");
  const dateStr = new Date().toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/40 font-display tracking-widest">
        Wird geladen …
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <div className="text-red-400 mb-4">{err}</div>
          <Link to="/dashboard" className="btn-primary">
            Zum Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="qr-print-page bg-white text-black min-h-screen">
      {/* Toolbar — nur auf Bildschirm sichtbar */}
      <div className="print:hidden sticky top-0 z-30 bg-[#050505] text-white border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <Link
            to={`/teams/${teamId}`}
            data-testid="qr-print-back-btn"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#CCFF00] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zum Team
          </Link>
          <div className="text-xs text-white/50 text-center hidden sm:block">
            Diese Seite ist für den Ausdruck optimiert. Verwende die Druckfunktion
            deines Browsers oder den Button rechts.
          </div>
          <button
            data-testid="qr-print-btn"
            onClick={() => window.print()}
            className="btn-primary !py-2 !px-4"
          >
            <Printer className="w-4 h-4" />
            Drucken
          </button>
        </div>
      </div>

      <div className="qr-sheet mx-auto max-w-[210mm] p-8 sm:p-12 print:p-0">
        {/* Header */}
        <header className="mb-8 pb-6 border-b-2 border-black flex items-start justify-between gap-6">
          <div className="flex-1 min-w-0">
            <div className="font-display text-xs tracking-[0.3em] text-[#5a7300] mb-1">
              / FOOTBALL MOTIVATION PROFILE · KABINEN-AUSHANG
            </div>
            <h1
              data-testid="qr-print-team-name"
              className="font-heading font-black text-3xl sm:text-4xl leading-tight tracking-tight text-black"
            >
              {team?.name}
            </h1>
            {(team?.age_group || team?.description) && (
              <div className="text-sm text-black/60 mt-1">
                {team?.age_group}
                {team?.description ? ` · ${team.description}` : ""}
              </div>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <div className="w-14 h-14 bg-[#CCFF00] flex items-center justify-center rounded-sm rotate-45 ml-auto">
              <span className="font-heading font-black text-black text-2xl -rotate-45">
                F
              </span>
            </div>
            <div className="font-display text-[10px] tracking-widest text-black/60 mt-2">
              {dateStr}
            </div>
          </div>
        </header>

        {/* Anleitung */}
        <div className="mb-6 p-4 border-2 border-black rounded-md bg-[#CCFF00]/10">
          <div className="font-heading font-black text-lg mb-1 text-black">
            So funktioniert&apos;s
          </div>
          <ol className="text-sm text-black/80 space-y-0.5 list-decimal list-inside">
            <li>Öffne die Kamera-App deines Smartphones.</li>
            <li>Richte die Kamera auf deinen QR-Code (kein Foto machen).</li>
            <li>Tippe auf den Link, der oben erscheint. Der Test öffnet sich automatisch.</li>
            <li>Dauer: ca. 10–12 Minuten. Antworte ehrlich – es gibt kein richtig oder falsch.</li>
          </ol>
        </div>

        {players.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-black/30 rounded-md">
            <div className="font-heading font-bold text-xl mb-2 text-black">
              Noch keine Spieler
            </div>
            <div className="text-sm text-black/60">
              Füge zuerst Spieler zum Team hinzu.
            </div>
          </div>
        ) : (
          <>
            {/* Grid: 3 Spalten × mehrere Zeilen, Print-optimiert */}
            <div className="qr-grid grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
              {players.map((p) => {
                const done = p.test_status === "completed";
                return (
                  <div
                    key={p.id}
                    data-testid={`qr-print-card-${p.id}`}
                    className="qr-card border-2 border-black rounded-md p-3 flex flex-col items-center text-center break-inside-avoid bg-white"
                  >
                    <div className="flex items-baseline justify-between w-full mb-2 px-1">
                      <span className="font-heading font-black text-xl text-black leading-none">
                        {p.jersey_number ? `#${p.jersey_number}` : "–"}
                      </span>
                      {done && (
                        <span className="font-display text-[8px] tracking-widest text-black/50">
                          ✓ FERTIG
                        </span>
                      )}
                    </div>
                    <div
                      className={`w-full aspect-square bg-white p-1.5 mb-2 flex items-center justify-center ${
                        done ? "opacity-25" : ""
                      }`}
                    >
                      <QRCode
                        value={testLink(p.test_token)}
                        size={220}
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                        level="M"
                        style={{
                          height: "auto",
                          maxWidth: "100%",
                          width: "100%",
                        }}
                      />
                    </div>
                    <div className="w-full min-h-[42px] flex flex-col justify-center">
                      <div className="font-heading font-black text-sm sm:text-base leading-tight text-black break-words">
                        {p.name}
                      </div>
                      {p.position && (
                        <div className="text-[10px] sm:text-xs text-black/60 mt-0.5">
                          {p.position}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t-2 border-black flex items-center justify-between text-xs text-black/60">
              <div>
                {players.length} Spieler · {pending.length} offen ·{" "}
                {players.length - pending.length} abgeschlossen
              </div>
              <div>fmp.albayrakmedia.de</div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 12mm;
          }
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
          }
          .qr-print-page {
            background: #ffffff !important;
          }
          .qr-sheet {
            padding: 0 !important;
            max-width: none !important;
          }
          .qr-grid {
            gap: 6mm !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
          .qr-card {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
